import { Injectable, computed, signal } from '@angular/core';
import { GameItem } from '../models/game-item.models';
import { apiBaseUrl, poe1 } from '../../game';
import { trackError } from '../app.config';

const prefix = poe1 ? 'poe1' : 'poe';
const KEY_SETTINGS = `${prefix}-random-loot-settings`;

export interface RandomLootItem {
  hash: string;
  item: GameItem;
  itemText: string | null;
}

export interface RandomLootSettings {
  areaLevel: number | null;
  size: number;
  includeIdentified: boolean;
}

interface ItemsApiRow {
  item_json: string;
  item_hash: string;
  item_text?: string | null;
}

export const RANDOM_LOOT_DEFAULT_SIZE = 50;
export const RANDOM_LOOT_MAX_SIZE = 200;
export const RANDOM_LOOT_RATE_LIMIT_MESSAGE =
  'Too many requests. Please wait 10 seconds before generating again.';
const RANDOM_LOOT_MIN_SIZE_LOW_AREA = 10;
const RANDOM_LOOT_MAX_SIZE_LOW_AREA = 50;
const RANDOM_LOOT_SIZE_LOCKED_MAX_AREA = 50;
const RATE_LIMIT_MAX_REQUESTS = 5;
const RATE_LIMIT_WINDOW_MS = 10_000;
const RATE_LIMIT_COOLDOWN_MS = 10_000;

@Injectable({
  providedIn: 'root',
})
export class RandomLootService {
  readonly items = signal<RandomLootItem[]>([]);
  readonly settings = signal<RandomLootSettings>({
    areaLevel: null,
    size: RANDOM_LOOT_DEFAULT_SIZE,
    includeIdentified: false,
  });
  readonly generating = signal(false);
  readonly rateLimitWarning = signal<string | null>(null);
  readonly rateLimitedUntil = signal<number | null>(null);
  readonly generateDisabled = computed(
    () =>
      this.generating() ||
      (this.rateLimitedUntil() !== null && Date.now() < this.rateLimitedUntil()!),
  );
  readonly sizeInputDisabled = computed(() => {
    const areaLevel = this.settings().areaLevel;
    return areaLevel !== null && areaLevel <= RANDOM_LOOT_SIZE_LOCKED_MAX_AREA;
  });

  private requestTimestamps: number[] = [];
  private cooldownTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.loadSettings();
  }

  static settingsForAreaLevel(
    areaLevel: number | null,
    includeIdentified = false,
  ): RandomLootSettings {
    const settings: RandomLootSettings = {
      areaLevel,
      size: RANDOM_LOOT_DEFAULT_SIZE,
      includeIdentified,
    };
    if (areaLevel !== null && areaLevel <= RANDOM_LOOT_SIZE_LOCKED_MAX_AREA) {
      settings.size = Math.min(
        RANDOM_LOOT_MAX_SIZE_LOW_AREA,
        Math.max(RANDOM_LOOT_MIN_SIZE_LOW_AREA, areaLevel),
      );
    }
    return settings;
  }

  effectiveSize(settings = this.settings()): number {
    const { areaLevel, size } = settings;
    if (areaLevel !== null && areaLevel <= RANDOM_LOOT_SIZE_LOCKED_MAX_AREA) {
      return Math.min(
        RANDOM_LOOT_MAX_SIZE_LOW_AREA,
        Math.max(RANDOM_LOOT_MIN_SIZE_LOW_AREA, areaLevel),
      );
    }
    return Math.min(RANDOM_LOOT_MAX_SIZE, Math.max(1, size));
  }

  setAreaLevel(areaLevel: number | null): void {
    this.persistSettings(
      RandomLootService.settingsForAreaLevel(areaLevel, this.settings().includeIdentified),
    );
  }

  setSize(size: number): void {
    if (this.sizeInputDisabled()) {
      return;
    }
    this.persistSettings({
      ...this.settings(),
      size: Math.min(RANDOM_LOOT_MAX_SIZE, Math.max(1, size)),
    });
  }

  setIncludeIdentified(includeIdentified: boolean): void {
    this.persistSettings({ ...this.settings(), includeIdentified });
  }

  async generateLootPile(overrides?: RandomLootSettings): Promise<boolean> {
    if (this.generateDisabled()) return false;

    const now = Date.now();
    this.pruneRequestTimestamps(now);
    if (this.requestTimestamps.length >= RATE_LIMIT_MAX_REQUESTS) {
      this.applyRateLimitCooldown();
      return false;
    }

    const settings = overrides ?? this.settings();
    this.requestTimestamps.push(now);
    this.generating.set(true);
    try {
      const params = new URLSearchParams();
      params.set('count', String(this.effectiveSize(settings)));
      params.set('include_identified', String(settings.includeIdentified));

      const { areaLevel } = settings;
      if (areaLevel !== null) {
        params.set('min_ilvl', String(areaLevel));
        params.set('max_ilvl', String(areaLevel + 2));
      }

      const response = await fetch(`${apiBaseUrl}/api/items?${params}`);

      if (response.status === 429) {
        this.applyRateLimitCooldown();
        return false;
      }

      if (!response.ok) {
        return false;
      }

      const rows = (await response.json()) as ItemsApiRow[];
      const items = rows
        .filter((row) => row.item_json && row.item_hash)
        .map((row) => ({
          hash: row.item_hash,
          item: new GameItem(JSON.parse(row.item_json) as Partial<GameItem>),
          itemText: row.item_text ?? null,
        }));

      this.items.set(items);
      return true;
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)));
      // Cross-origin calls from localhost: edge 429s often lack CORS headers, so fetch
      // rejects instead of returning response.status === 429. Treat as rate-limited locally.
      if (apiBaseUrl) {
        this.applyRateLimitCooldown();
      }
      return false;
    } finally {
      this.generating.set(false);
    }
  }

  private pruneRequestTimestamps(now: number): void {
    this.requestTimestamps = this.requestTimestamps.filter(
      (timestamp) => now - timestamp < RATE_LIMIT_WINDOW_MS,
    );
  }

  private applyRateLimitCooldown(): void {
    if (this.cooldownTimer !== undefined) {
      clearTimeout(this.cooldownTimer);
    }

    this.rateLimitedUntil.set(Date.now() + RATE_LIMIT_COOLDOWN_MS);
    this.rateLimitWarning.set(RANDOM_LOOT_RATE_LIMIT_MESSAGE);

    this.cooldownTimer = setTimeout(() => {
      this.rateLimitedUntil.set(null);
      this.rateLimitWarning.set(null);
      this.cooldownTimer = undefined;
    }, RATE_LIMIT_COOLDOWN_MS);
  }

  private loadSettings(): void {
    try {
      const raw = localStorage.getItem(KEY_SETTINGS);
      if (!raw) return;

      const parsed = JSON.parse(raw) as Partial<RandomLootSettings>;
      const areaLevel =
        typeof parsed.areaLevel === 'number' && Number.isFinite(parsed.areaLevel)
          ? Math.round(parsed.areaLevel)
          : null;
      const size =
        typeof parsed.size === 'number' && Number.isFinite(parsed.size)
          ? Math.min(RANDOM_LOOT_MAX_SIZE, Math.max(1, Math.round(parsed.size)))
          : RANDOM_LOOT_DEFAULT_SIZE;
      const includeIdentified = parsed.includeIdentified === true;

      const settings: RandomLootSettings = { areaLevel, size, includeIdentified };
      if (areaLevel !== null && areaLevel <= RANDOM_LOOT_SIZE_LOCKED_MAX_AREA) {
        settings.size = Math.min(
          RANDOM_LOOT_MAX_SIZE_LOW_AREA,
          Math.max(RANDOM_LOOT_MIN_SIZE_LOW_AREA, areaLevel),
        );
      }

      this.settings.set(settings);
    } catch {
      this.settings.set({ areaLevel: null, size: RANDOM_LOOT_DEFAULT_SIZE, includeIdentified: false });
    }
  }

  private persistSettings(settings: RandomLootSettings): void {
    this.settings.set(settings);
    localStorage.setItem(KEY_SETTINGS, JSON.stringify(settings));
  }
}
